import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebase";
import logo from "./assets/logo.png";
import googleLogo from "./assets/google.svg";
import { Capacitor } from "@capacitor/core";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signOut
} from "firebase/auth";

function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [view, setView] = useState("login");
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  const login = async () => {
    try {
      setMessage("");

      if (!email || !password) {
        setMessage("Enter email and password first.");
        return;
      }

      const result = await signInWithEmailAndPassword(auth, email, password);

      await result.user.reload();

      if (!result.user.emailVerified) {
        await sendEmailVerification(result.user);
        await signOut(auth);

        setView("verify");
        setMessage("We sent you another verification email.");
        return;
      }

      navigate("/");
    } catch (error) {
      setMessage(error.message);
      console.log(error);
    }
  };

  const signup = async () => {
    try {
      setMessage("");

      if (!email || !password) {
        setMessage("Enter email and password first.");
        return;
      }

      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      await sendEmailVerification(result.user);
      await signOut(auth);

      setView("verify");
    } catch (error) {
      setMessage(error.message);
      console.log(error);
    }
  };

  const resendVerification = async () => {
    try {
      setMessage("");

      if (!email || !password) {
        setMessage("Enter your email and password first, then click resend.");
        return;
      }

      const result = await signInWithEmailAndPassword(auth, email, password);

      await result.user.reload();

      if (result.user.emailVerified) {
        await signOut(auth);
        setView("login");
        setMessage("Your email is already verified. You can log in now.");
        return;
      }

      await sendEmailVerification(result.user);
      await signOut(auth);

      setMessage("Verification email sent again. Please check your inbox.");
    } catch (error) {
      setMessage(error.message);
      console.log(error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setMessage("");

      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        const { FirebaseAuthentication } = await import(
          "@capacitor-firebase/authentication"
        );

const result = await FirebaseAuthentication.signInWithGoogle();

const idToken =
  result.credential?.idToken ||
  result.idToken ||
  result.user?.authentication?.idToken;

const accessToken =
  result.credential?.accessToken ||
  result.accessToken ||
  result.user?.authentication?.accessToken;

if (!idToken && !accessToken) {
  console.log("Google native result:", result);
  setMessage("Google sign-in did not return a usable token.");
  return;
}

const credential = GoogleAuthProvider.credential(idToken, accessToken);

await signInWithCredential(auth, credential);

        navigate("/");
        return;
      }

      const provider = new GoogleAuthProvider();

      provider.setCustomParameters({
        prompt: "select_account"
      });

      await signInWithPopup(auth, provider);

      navigate("/");
    } catch (error) {
      console.log("Google sign-in error:", error);

      if (error.code === "auth/popup-closed-by-user") {
        return;
      }

      setMessage(error.message);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setMessage("");

      if (!email) {
        setMessage("Enter your email first.");
        return;
      }

      await sendPasswordResetEmail(auth, email);

      setView("password-reset");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const goBackToLogin = () => {
    setView("login");
    setMessage("");
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <img src={logo} alt="MyBuildID" style={logoImageStyle} />

        {view === "login" && (
          <>
            <p style={taglineStyle}>
              Your automotive build. One scan away.
            </p>

            <button onClick={handleGoogleLogin} style={googleButtonStyle}>
              <img src={googleLogo} alt="Google" style={googleIconStyle} />
              Continue with Google
            </button>

            <div style={dividerStyle}>
              <span style={dividerLineStyle}></span>
              <span style={dividerTextStyle}>or</span>
              <span style={dividerLineStyle}></span>
            </div>

            <input
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />

            <input
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
            />

            {message && <p style={messageStyle}>{message}</p>}

            <button onClick={login} style={primaryButtonStyle}>
              Log In
            </button>

            <button onClick={handlePasswordReset} style={forgotPasswordStyle}>
              Forgot Password?
            </button>

            <p style={helperTextStyle}>
              Don&apos;t have an account?{" "}
              <button onClick={signup} style={linkButtonStyle}>
                Create one
              </button>
            </p>
          </>
        )}

        {view === "verify" && (
          <>
            <h2 style={successTitleStyle}>🎉 Welcome to MyBuildID</h2>

            <p style={successTextStyle}>
              We&apos;ve sent a verification email to:
            </p>

            <p style={emailTextStyle}>{email}</p>

            <p style={successTextStyle}>
              Please verify your email before signing in. Once verified, return
              here and log in to create your garage.
            </p>

            {message && <p style={messageStyle}>{message}</p>}

            <button onClick={resendVerification} style={primaryButtonStyle}>
              Resend Verification Email
            </button>

            <button onClick={goBackToLogin} style={secondaryButtonStyle}>
              Back to Login
            </button>
          </>
        )}

        {view === "password-reset" && (
          <>
            <h2 style={successTitleStyle}>Password Reset Sent</h2>

            <p style={successTextStyle}>
              We sent a password reset email to:
            </p>

            <p style={emailTextStyle}>{email}</p>

            <p style={successTextStyle}>
              Check your inbox and follow the link to reset your password.
            </p>

            {message && <p style={messageStyle}>{message}</p>}

            <button onClick={goBackToLogin} style={primaryButtonStyle}>
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  backgroundColor: "#111",
  color: "white",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontFamily: "Arial",
  padding: "20px"
};

const cardStyle = {
  backgroundColor: "#0a0a0a",
  padding: "32px",
  borderRadius: "20px",
  width: "100%",
  maxWidth: "380px",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  textAlign: "center"
};

const logoImageStyle = {
  width: "100%",
  maxWidth: "760px",
  margin: "0 auto 10px",
  display: "block",
  backgroundColor: "transparent"
};

const taglineStyle = {
  opacity: 0.75,
  marginTop: "-4px",
  marginBottom: "14px"
};

const inputStyle = {
  padding: "14px",
  borderRadius: "10px",
  border: "none",
  fontSize: "16px"
};

const primaryButtonStyle = {
  padding: "14px",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "16px",
  backgroundColor: "#e10600",
  color: "white"
};

const secondaryButtonStyle = {
  padding: "14px",
  border: "1px solid #333",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "16px",
  backgroundColor: "#181818",
  color: "white"
};

const googleButtonStyle = {
  padding: "14px",
  border: "1px solid #333",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "16px",
  backgroundColor: "white",
  color: "#111",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px"
};

const dividerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  opacity: 0.6,
  margin: "4px 0"
};

const dividerLineStyle = {
  flex: 1,
  height: "1px",
  backgroundColor: "#333"
};

const dividerTextStyle = {
  fontSize: "13px"
};

const helperTextStyle = {
  opacity: 0.8,
  fontSize: "14px"
};

const linkButtonStyle = {
  background: "none",
  border: "none",
  color: "white",
  textDecoration: "underline",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "14px"
};

const forgotPasswordStyle = {
  backgroundColor: "transparent",
  border: "none",
  color: "#999",
  cursor: "pointer",
  marginTop: "10px"
};

const googleIconStyle = {
  width: "20px",
  height: "20px",
  flexShrink: 0
};

const messageStyle = {
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "10px",
  padding: "12px",
  color: "#ffb3b3",
  fontSize: "14px",
  lineHeight: "1.4"
};

const successTitleStyle = {
  fontSize: "24px",
  margin: "4px 0 6px"
};

const successTextStyle = {
  opacity: 0.8,
  lineHeight: "1.55",
  margin: "0"
};

const emailTextStyle = {
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "10px",
  padding: "12px",
  fontWeight: "bold",
  wordBreak: "break-word"
};

export default Auth;